import React, { useEffect, useState, Suspense } from 'react'
import { Layers } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AuthProvider, useAuth } from './lib/AuthContext'
import { CommunityProvider, useCommunity } from './lib/CommunityContext'
import { SubscriptionProvider } from './lib/SubscriptionContext'
import { ThemeProvider } from './lib/ThemeContext'
import { SidebarProvider } from './lib/SidebarContext'
import { RouteProvider, useRoute } from './lib/RouteContext'
import { MenuProvider, MenuRealtimeSync, useMenu } from './lib/MenuContext'
import { LocaleProvider } from './lib/LocaleContext'
import { ProfileProvider } from './components/features/profile/ProfileProvider'
import { Header } from './components/layout/Header'
import { Sidebar } from './components/layout/Sidebar'
import { SidebarRail } from './components/layout/SidebarRail'
import { TopNavFooter, TopNavStrip } from './components/layout/TopNav'
import { DebugPanel } from './components/features/debug/DebugPanel'
import { EventToastContainer } from './components/features/events/EventToastContainer'
import { EmailNoticeToaster } from './components/features/notices/EmailNoticeToaster'
import { ScheduledToaster } from './components/features/posts/schedule/ScheduledToaster'
import { PlanLimitToaster } from './components/features/entitlements/PlanLimitToaster'
import { ErrorBoundary } from './components/shared/ErrorBoundary'
import { SdkGate } from './components/shared/SdkGate'
import { UpdateNotice } from './components/shared/UpdateNotice'
import { GalleryUploadPill } from '@/components/features/gallery/GalleryUploadProgress'
import { Button } from './components/ui/button'
import { sdk } from './lib/sdk'
import { useSidebar } from './lib/SidebarContext'
import { cn } from './lib/utils'
import { setSentryUser, setSentryCommunity, clearSentryUser } from './lib/sentry'
import { ChangePasswordDialog } from './components/features/auth/ChangePasswordDialog'
import { CompleteProfileModal } from '@/components/features/members/CompleteProfileModal'
import { WelcomeGate } from '@/components/features/welcome/WelcomeGate'
import { PendingSurveysNotice } from '@/components/features/surveys/PendingSurveysNotice'
import { MoodPulseHome } from '@/components/features/surveys/MoodPulseViews'
import { useGoogleAnalytics } from './lib/hooks/useGoogleAnalytics'
import { CookieNotice } from './components/features/cookies/CookieNotice'
import { usePerformanceTracking } from './lib/hooks/usePerformanceTracking'

import { isKioskPath } from '@/lib/kioskPath'
import { isEventKioskPath, eventCheckInIdOf, ORGANIZING_EVENTS_PATH } from '@/lib/eventKioskPath'
import { isBookingNewPath, isRoomPanelPath } from '@/lib/roomPanelPath'
import { isSupportTicketPath } from './lib/supportPath'
import { isEpisodeLinkPath } from '@/modules/podcast/engine/episodeLink'
import { isVerifyPath } from '@/lib/verifyPath'
import { isPublicSurveyPath, isSurveyAnswerPath } from '@/lib/surveyPaths'
import { isFormLinkPath, isPublicFormPath } from '@/lib/formPaths'
import { isMediaEmbedPath } from '@/lib/mediaEmbedPath'
import { isInviteDeclinePath } from '@/lib/inviteDeclinePath'
import { isEmailUnsubscribePath } from '@/lib/emailUnsubscribePath'
import { adminCommunityIdOf, isAdminCommunitiesPath } from '@/lib/adminCommunitiesPath'
import { adminCommunityRequestIdOf, isAdminCommunityRequestsPath } from '@/lib/adminCommunityRequestsPath'
import { ROUTES } from '@/lib/routes'
import { getModule, renderSpaceDetail, renderSpacePage } from '@/modules/registry'
import type { SpacePageProps } from '@/modules/types'
import { isChangePlanPath, isPricingPath, PRICING_PATH } from './lib/checkout/pricingPath'
import { FlashToastHost } from './components/ui/flash-toast'
import { isOfferPath } from './lib/downsellPath'
import { isPaymentPath } from './lib/checkout/pendingCheckout'
import { SuspensionBanner } from './components/features/memberEntry/SuspensionBanner'
import { isSettingsPath } from './components/features/settings/settingsSections'
import { isMemberPagePath } from '@/lib/memberPage'
import { SendingReputationBanner } from './components/features/sendingReputation/SendingReputationBanner'
import { ErasureBanner } from './components/features/privacy/ErasureBanner'
import { TwoFactorBanner } from './components/features/twoFactor/TwoFactorBanner'
import { StepUpHost } from './components/features/twoFactor/StepUpHost'
import { isAccountErasureCancelPath } from '@/lib/erasure'
import { privacyExportIdOf } from '@/lib/personalDataExport'
import { ProfilePhotoBar } from './components/features/profile/ProfilePhotoBar'
import { PushPrompts } from './components/features/push/PushPrompts'
import { isProductPath, isWaitlistPath } from './lib/products/paths'
import { BootErrorScreen, type BootFailure } from './components/BootErrorScreen'
import { SpaceAccessDenied } from './components/features/spaces/SpaceAccessDenied'
import { AdminOnlyNotice } from './components/features/spaces/AdminOnlyNotice'
import { ItemUnavailable } from './components/shared/ItemUnavailable'
import { getApiOrigin, getSdkUrl } from './lib/sdk-official/loader'
import { lazyPage } from '@/lib/lazyPage'
/* Preloadable: the plans page, the Store and a padlocked space's offer fetch it ahead of the click. */
import { CheckoutSubscriptionsPage } from '@/lib/checkout/planCheckoutNav'
import { ChatProvider } from '@/components/features/chat/ChatProvider'
import { RealtimeProvider } from '@/lib/realtime/RealtimeProvider'
import { ScreenPresenceProvider } from '@/lib/realtime/ScreenPresence'
import { StaleSaveDialogHost } from '@/components/features/realtime/EditConflictNotices'
import { ChatDock, ChatMobileSheet, CHAT_SIDE_WIDTH_CLASS, useChatSideOpen } from '@/components/features/chat/ChatDock'
import { MiniPlayer, PlayerProvider, VideoDockProvider } from '@/lib/player'

/* Every page loads on first use, so the entry carries only the shell. */
const InviteDeclinePage = lazyPage(() => import('@/pages/InviteDecline'), 'InviteDeclinePage')
const EmailUnsubscribePage = lazyPage(() => import('@/pages/EmailUnsubscribe'), 'EmailUnsubscribePage')
const SectionPage = lazyPage(() => import('@/pages/SectionPage'), 'SectionPage')
const ContentPage = lazyPage(() => import('./pages/Content'), 'ContentPage')
const KioskPage = lazyPage(() => import('@/pages/Kiosk'), 'KioskPage')
const EventKioskPage = lazyPage(() => import('@/pages/EventKiosk'), 'EventKioskPage')
const EventCheckInLinkPage = lazyPage(() => import('@/pages/EventCheckInLink'), 'EventCheckInLinkPage')
const OrganizingEventsPage = lazyPage(() => import('@/pages/OrganizingEvents'), 'OrganizingEventsPage')
const RoomPanelPage = lazyPage(() => import('@/pages/RoomPanel'), 'RoomPanelPage')
const BookingNewPage = lazyPage(() => import('@/pages/BookingNew'), 'BookingNewPage')
const MyDownloadsPage = lazyPage(() => import('@/pages/MyDownloads'), 'MyDownloadsPage')
const MyParcelsPage = lazyPage(() => import('@/pages/MyParcels'), 'MyParcelsPage')
const MyDayPassesPage = lazyPage(() => import('@/pages/MyDayPasses'), 'MyDayPassesPage')
const MyCompanyPage = lazyPage(() => import('@/pages/MyCompany'), 'MyCompanyPage')
const SupportTicketLinkPage = lazyPage(() => import('@/pages/SupportTicketLink'), 'SupportTicketLinkPage')
/* F-178: the link of the "new episode" e-mail, /_/episodes/{spaceId}/{episodeId}. */
/* F-178 phase 2: the public embed (`/embed/episode|show/{id}`), framed by other sites. */
const MediaEmbedPage = lazyPage(() => import('@/pages/MediaEmbed'), 'MediaEmbedPage')
const EpisodeLinkPage = lazyPage(() => import('@/modules/podcast/engine/EpisodeLinkPage'), 'EpisodeLinkPage')
const VerifyCertificatePage = lazyPage(() => import('@/pages/VerifyCertificate'), 'VerifyCertificatePage')
const SurveyAnswerPage = lazyPage(() => import('@/pages/SurveyAnswer'), 'SurveyAnswerPage')
const RecognitionLinkPage = lazyPage(() => import('@/pages/RecognitionLink'), 'RecognitionLinkPage')
/** F-177: the member page, `/_/members/{id}`; `/achievements` (the F-148 badge e-mail link) opens it on Conquistas. */
const MemberPage = lazyPage(() => import('@/pages/MemberPage'), 'MemberPage')
const ExportDownloadPage = lazyPage(() => import('@/pages/ExportDownload'), 'ExportDownloadPage')
const AccountErasureCancelPage = lazyPage(() => import('@/pages/AccountErasureCancel'), 'AccountErasureCancelPage')
const downloadPrivacyExport = (exportId: string) => sdk.privacy.downloadExport(exportId)
const FeedbackLinkPage = lazyPage(() => import('@/pages/FeedbackLink'), 'FeedbackLinkPage')
const DevelopmentPlanLinkPage = lazyPage(() => import('@/pages/DevelopmentPlanLink'), 'DevelopmentPlanLinkPage')
const ChatLinkPage = lazyPage(() => import('@/pages/ChatLink'), 'ChatLinkPage')
const NotificationsLinkPage = lazyPage(() => import('@/pages/NotificationsLink'), 'NotificationsLinkPage')
const TimeTrackingLinkPage = lazyPage(() => import('@/pages/TimeTrackingLink'), 'TimeTrackingLinkPage')
const PublicSurveyPage = lazyPage(() => import('@/pages/PublicSurvey'), 'PublicSurveyPage')
const PublicFormPage = lazyPage(() => import('@/pages/PublicForm'), 'PublicFormPage')
const FormLinkPage = lazyPage(() => import('@/pages/FormLink'), 'FormLinkPage')
const EventsPage = lazyPage(() => import('./pages/Events'), 'EventsPage')
const CoursesPage = lazyPage(() => import('./pages/Courses'), 'CoursesPage')
const RankingPage = lazyPage(() => import('./pages/Ranking'), 'RankingPage')
const GalleryPage = lazyPage(() => import('./pages/Gallery'), 'GalleryPage')
const MembersPage = lazyPage(() => import('./pages/Members'), 'MembersPage')
const SettingsPage = lazyPage(() => import('./pages/Settings'), 'SettingsPage')
const MonetizationPage = lazyPage(() => import('./pages/Monetization'), 'MonetizationPage')
const DashboardPage = lazyPage(() => import('./pages/Dashboard'), 'DashboardPage')
const CampaignsPage = lazyPage(() => import('./pages/Campaigns'), 'CampaignsPage')
const WorkflowsPage = lazyPage(() => import('./pages/Workflows'), 'WorkflowsPage')
const LogsPage = lazyPage(() => import('./pages/Logs'), 'LogsPage')
const MediaLibraryPage = lazyPage(() => import('./pages/MediaLibrary'), 'MediaLibraryPage')
const ScheduledPostsPage = lazyPage(() => import('./pages/ScheduledPosts'), 'ScheduledPostsPage')
const EngagementPage = lazyPage(() => import('./pages/Engagement'), 'EngagementPage')
const AnalyticsPage = lazyPage(() => import('./pages/Analytics'), 'AnalyticsPage')
const AccountPage = lazyPage(() => import('./pages/Account'), 'AccountPage')
const ChangePlanPage = lazyPage(() => import('./pages/ChangePlan'), 'ChangePlanPage')
const PricingPlansPage = lazyPage(() => import('./pages/PricingPlans'), 'PricingPlansPage')
const DownsellOfferPage = lazyPage(() => import('./pages/DownsellOffer'), 'DownsellOfferPage')
const SetPasswordPage = lazyPage(() => import('./pages/SetPassword'), 'SetPasswordPage')
const SupportEntryPage = lazyPage(() => import('./pages/SupportEntry'), 'SupportEntryPage')
const OAuthAuthorizePage = lazyPage(() => import('./pages/OAuthAuthorize'), 'OAuthAuthorizePage')
const AdminFeesPage = lazyPage(() => import('./pages/AdminFees'), 'AdminFeesPage')
const AdminPlatformPage = lazyPage(() => import('./pages/AdminPlatform'), 'AdminPlatformPage')
const AdminCommunitiesHub = lazyPage(() => import('./pages/AdminCommunitiesHub'), 'AdminCommunitiesHub')
const AdminCommunityDetailPage = lazyPage(() => import('./pages/AdminCommunityDetail'), 'AdminCommunityDetailPage')
const AdminCommunityRequestDetailPage = lazyPage(() => import('./pages/AdminCommunityRequestDetail'), 'AdminCommunityRequestDetailPage')
const PaymentStatusPage = lazyPage(() => import('./pages/PaymentStatus'), 'PaymentStatusPage')
const ProductPage = lazyPage(() => import('./pages/ProductPage'), 'ProductPage')
const WaitlistPage = lazyPage(() => import('./pages/WaitlistPage'), 'WaitlistPage')
const CheckoutProductsPage = lazyPage(() => import('./pages/CheckoutProducts'), 'CheckoutProductsPage')
const CheckoutSuccessPage = lazyPage(() => import('./pages/CheckoutSuccess'), 'CheckoutSuccessPage')

/**
 * What kind of failure this was.
 *
 * The distinction matters because the two have different readers. A domain
 * nobody registered is ours to fix; an API that cannot be reached from one
 * network is theirs, and the only way they find that out is if the screen says
 * which address did not answer instead of blaming the domain they typed.
 */
const classifyBootFailure = (error: unknown): BootFailure => {
  const failure = error as { code?: string; url?: string; message?: string; response?: { status?: number } }

  /* The SDK script never loaded. The browser gives no status and no message
     for a cross-origin script, so the tag the loader sets is all there is —
     and on a corporate network this is nearly always a proxy. */
  if (failure?.code === 'SDK_UNREACHABLE') {
    return { kind: 'unreachable', url: failure.url || getSdkUrl(), detail: failure.message }
  }

  const status = failure?.response?.status

  /* A 404 from the API is the server saying this domain has no community —
     the same answer as a null body, by a different route. */
  if (status === 404) return { kind: 'notFound' }

  if (status) {
    return { kind: 'server', status, url: `${getApiOrigin()}/api/communities/domain`, detail: failure?.message }
  }

  /* No status at all: the request never completed. Blocked, offline, DNS,
     TLS interception — all of them look like this from in here. */
  return { kind: 'unreachable', url: `${getApiOrigin()}/api/communities/domain`, detail: failure?.message }
}

/**
 * What a lazy page shows while its chunk arrives. It is the same centered
 * spinner the pages draw while their data loads, so the swap between the two
 * does not move anything.
 */
const PageFallback = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
)

const AppContent = () => {
  const { t } = useTranslation('common')
  const { currentPath, spaceData, isLoadingSpace, params, navigate, deniedSpace, isResolvingRoute } = useRoute()
  const { sections, isLoading: isLoadingMenu, getHomeSpace } = useMenu()
  const { hasRole } = useAuth()
  const canEdit = hasRole(['owner', 'admin'])
  const seesWholeMenu = hasRole(['owner', 'admin', 'moderator', 'finance'])
  const [homeSpaceData, setHomeSpaceData] = useState<SpacePageProps['spaceData'] | null>(null)
  const [isLoadingHomeSpace, setIsLoadingHomeSpace] = useState(false)

  /* The home: the first space of the home section this person can open (F-161a:
     never a padlocked one, so a member without a plan never lands on an offer). */
  const defaultHomeSpace = React.useMemo(() => getHomeSpace() ?? undefined, [getHomeSpace])

  /* F-161a 7b: "only the team sees it", on top of the space, from the API's `adminOnly`. */
  const withAdminOnlyBanner = (spaceId: string | undefined, page: React.ReactNode) => {
    const listed = sections.flatMap((section) => section.spaces || []).find((space) => space.id === spaceId)
    return listed?.adminOnly
      ? <><AdminOnlyNotice variant="banner" />{page}</>
      : page
  }

  // Fetch home space data when on root path
  useEffect(() => {
    if (currentPath === '/' && defaultHomeSpace && !homeSpaceData) {
      setIsLoadingHomeSpace(true)
      sdk.spaces.get(defaultHomeSpace.id)
        .then(space => {
          if (space) {
            setHomeSpaceData(space)
          }
        })
        .catch(error => {
          console.error('Error fetching home space:', error)
        })
        .finally(() => {
          setIsLoadingHomeSpace(false)
        })
    } else if (currentPath !== '/') {
      // Clear home space data when leaving root path
      setHomeSpaceData(null)
    }
  }, [currentPath, defaultHomeSpace?.id])

  /* APP-173: the address names a listed space that is not on screen yet. Right after a click the
     route effect has not run (spaceData is the previous route's or null, isLoadingSpace false), and
     while it fetches spaceData is still the previous space: either way the switch below drew the home
     space or the previous module (a Feed asking for the new address: "Error loading feed"). Until the
     space arrives, the standard loading state. */
  const pathSegments = currentPath.split(/[?#]/)[0].split('/').filter(Boolean)
  const isSpacePath = pathSegments.length === 2 || pathSegments.length === 3 || (pathSegments.length === 5 && pathSegments[3] === 'lessons')
  const routeSpaceId = isSpacePath
    ? sections.find((section) => section.slug === pathSegments[0])?.spaces?.find((space) => space.slug === pathSegments[1])?.id
    : undefined
  if (routeSpaceId && spaceData?.id !== routeSpaceId && !deniedSpace && (isResolvingRoute || isLoadingSpace)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading')}</p>
        </div>
      </div>
    )
  }

  // Handle dynamic routes (section/space or section/space/item). Which page a
  // module draws comes from the registry (`src/modules`), not from a switch here.
  if (spaceData) {
    // Detail route (3 segments: section/space/itemId); a module without one falls back to its list.
    if (params.itemId) {
      const detail = renderSpaceDetail(spaceData, params)
      if (detail) return withAdminOnlyBanner(spaceData.id, detail)
    }
    // Regular space list view (2 segments: section/space), also while the space is still loading.
    // A module without a detail view opens the item itself (`itemId`), so it stays mounted while it loads.
    if (!isLoadingSpace || !params.itemId || !getModule(spaceData.module)?.renderDetail) {
      return withAdminOnlyBanner(spaceData.id, renderSpacePage(spaceData.module, { spaceId: spaceData.id, spaceData, isLoadingSpace, itemId: params.itemId }))
    }
  }

  // Show loading state on initial fetch (when loading but no data yet)
  if (isLoadingSpace && !spaceData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading')}</p>
        </div>
      </div>
    )
  }

  /* APP-55: the API refused this space (403). Without this the route fell
     through to the default case and drew the home space, with no word on why
     nor on how to get in. */
  if (deniedSpace && !isLoadingSpace) {
    return <SpaceAccessDenied denied={deniedSpace} />
  }

  // Handle static routes
  // Plan links (/pricing/{plan}/{option}) are the pricing page with a selection.
  // The route is the path alone: /checkout-success?product=… is /checkout-success.
  const routePath = currentPath.split('?')[0]
  // The offer link carries a token after the prefix: /_/downsell/offer/{token} (F-39), with no session.
  if (isOfferPath(routePath)) return <DownsellOfferPage />
  // The confirmation of a checkout order: /_/checkout/payment/{purchaseId}.
  if (isPaymentPath(routePath)) return <PaymentStatusPage />
  // F-195: the plan change with the checkout's layout: /_/subscriptions/change-plan/{plan}/{option}.
  if (isChangePlanPath(routePath)) return <ChangePlanPage />
  // F-74: the links of the access and waitlist e-mails; both need a session.
  if (isProductPath(routePath)) return <ProductPage />
  if (isWaitlistPath(routePath)) return <WaitlistPage />
  // F-75: "My downloads", reached from "My purchases".
  if (routePath === ROUTES.myDownloads) return <MyDownloadsPage />
  if (routePath === ROUTES.myParcels) return <MyParcelsPage />
  // F-249: "Meus passes", the link of the pass e-mails and of Presença.
  if (routePath === ROUTES.myDayPasses) return <MyDayPassesPage />
  // F-93 1A: "My company"; the companies themselves are the Empresas tab of Membros.
  if (routePath === ROUTES.myCompany) return <MyCompanyPage />
  // F-82: the link of the Support e-mails; it needs a session and moves to the ticket in its space.
  if (isSupportTicketPath(routePath)) return <SupportTicketLinkPage />
  // F-178: the link of the "new episode" e-mail: /_/episodes/{spaceId}/{episodeId}; it moves to the episode in its space.
  if (isEpisodeLinkPath(routePath)) return <EpisodeLinkPage />
  // A certificate's public verification page: /_/certificates/verify/{code} (F-42), with no session.
  if (isVerifyPath(routePath)) return <VerifyCertificatePage />
  // F-103: a member answers a survey of their community: /_/surveys/{id}.
  if (isSurveyAnswerPath(routePath)) return <SurveyAnswerPage />
  // F-109: the links of the forms e-mails: /_/forms/{id} and /_/forms/{id}/submissions/{submissionId}.
  if (isFormLinkPath(routePath)) return <FormLinkPage />
  // F-115: an event's door without its space (organizers who cannot open the Events space): /_/events/{id}/check-in.
  if (eventCheckInIdOf(routePath)) return <EventCheckInLinkPage />
  if (routePath === ORGANIZING_EVENTS_PATH) return <OrganizingEventsPage />
  // F-53: the link of the "you were recognized" e-mail: /_/recognitions/{id}; it needs a session.
  if (routePath.startsWith(ROUTES.recognition)) return <RecognitionLinkPage />
  // F-177: the member page; F-148's badge e-mail link /_/members/{id}/achievements opens it on Conquistas.
  if (isMemberPagePath(routePath)) return <MemberPage />
  // SEC-216: the link of the "your file is ready" e-mail: /_/exports/{id}; requester only, it needs a session.
  if (routePath.startsWith(ROUTES.exportDownload)) return <ExportDownloadPage />
  // F-200: the link of the "your data is ready" e-mail: /privacy/exports/{id}; only the person whose data it holds.
  const privacyExportId = privacyExportIdOf(routePath)
  if (privacyExportId) return <ExportDownloadPage key={privacyExportId} exportId={privacyExportId} fetchLink={downloadPrivacyExport} />
  // F-49: the links of the feedback e-mails: /_/feedbacks/{id} and /_/feedbacks/requests/{id}; author and recipient only.
  if (routePath.startsWith(ROUTES.feedback)) return <FeedbackLinkPage />
  // F-52: the link of the PDI e-mails: /_/development-plans/{id}; who reads it is the API's (anyone else: 404).
  if (routePath.startsWith(ROUTES.developmentPlan)) return <DevelopmentPlanLinkPage />
  // F-51: the links of the time tracking e-mails: /_/time-tracking/adjustments/{id} and /_/time-tracking/{spaceId}/timesheet?month=AAAA-MM.
  if (routePath.startsWith(ROUTES.timeTracking)) return <TimeTrackingLinkPage />
  // F-108: the link of the unread-message e-mail: /_/chat/{conversationId} opens the chat panel on it.
  if (routePath.startsWith(ROUTES.chat)) return <ChatLinkPage />
  // F-186 phase 2: the push of a team notice (and the burst summary) opens the bell: /_/notifications.
  if (routePath === ROUTES.notifications) return <NotificationsLinkPage />
  // F-85: "thanks, but I do not want to join" from the import invite e-mail: /_/invite/decline/{token}, with no session.
  if (isInviteDeclinePath(routePath)) return <InviteDeclinePage />
  // F-104: "no more notices of <category>" from a notice's footer: /_/email/unsubscribe/{token}, with no session.
  if (isEmailUnsubscribePath(routePath)) return <EmailUnsubscribePage />
  // F-199: "Desistir" from the account erasure e-mail: /account-erasure/cancel?token=…, with no session.
  if (isAccountErasureCancelPath(routePath)) return <AccountErasureCancelPage />
  // F-104: Minha Conta › E-mails desta comunidade, opened from that page (?category= highlights one).
  if (routePath === ROUTES.accountEmails) return <AccountPage />
  // F-79 1C: where the door panel's QR lands; it needs a session and opens the booking dialog filled in.
  if (isBookingNewPath(routePath)) return <BookingNewPage />
  // F-40: an AI assistant asking for MCP access; the OAuth query is passed back untouched.
  if (routePath === ROUTES.mcpAuthorize) return <OAuthAuthorizePage />
  // F-65: the SuperAdmin's Communities section. The pages check the role before any request; the API does too.
  // F-89: the SuperAdmin's community requests (approve with a plan, or reject): the Pedidos tab of Comunidades.
  if (isAdminCommunityRequestsPath(routePath)) {
    const requestId = adminCommunityRequestIdOf(routePath)
    return requestId ? <AdminCommunityRequestDetailPage key={requestId} requestId={requestId} /> : <AdminCommunitiesHub tab="requests" />
  }
  if (isAdminCommunitiesPath(routePath)) {
    const communityId = adminCommunityIdOf(routePath)
    return communityId ? <AdminCommunityDetailPage key={communityId} communityId={communityId} /> : <AdminCommunitiesHub tab="communities" />
  }
  // UX review 2026-10-27: Settings has one page per topic, /settings/{slug}.
  if (isSettingsPath(routePath)) return <SettingsPage />
  switch (isPricingPath(routePath) ? PRICING_PATH : routePath) {
    case '/':
      if (!defaultHomeSpace) {
        if (isLoadingMenu) {
          return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-600">{t('loading')}</p>
              </div>
            </div>
          )
        }
        return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
            <div className="max-w-sm text-center">
              <div className="w-14 h-14 mx-auto mb-5 bg-primary/10 rounded-2xl flex items-center justify-center">
                <Layers className="w-7 h-7 text-primary" />
              </div>
              {canEdit ? (
                <>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('emptyState.noSectionsTitle')}</h2>
                  <p className="text-sm text-gray-500 mb-6">{t('emptyState.noSectionsDescAdmin')}</p>
                  <Button
                    onClick={() => navigate('/settings')}
                    className="bg-primary text-white hover:bg-primary/90 text-sm"
                  >
                    {t('emptyState.goToSettings')}
                  </Button>
                </>
              ) : (
                <>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('emptyState.noSectionsTitle')}</h2>
                  <p className="text-sm text-gray-500">{t('emptyState.noSectionsDescMember')}</p>
                </>
              )}
            </div>
          </div>
        )
      }
      if (isLoadingHomeSpace) {
        return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-gray-600">{t('loading')}</p>
            </div>
          </div>
        )
      }
      // Render appropriate page based on home space module
      /* F-241: the mood pulse card (and the leader's team trend) on top of the Home. */
      return <>
        <MoodPulseHome />
        {withAdminOnlyBanner(defaultHomeSpace.id, renderSpacePage(defaultHomeSpace.module, { spaceId: defaultHomeSpace.id, spaceData: homeSpaceData, isLoadingSpace: false }))}
      </>
    case '/content':
      return <ContentPage />
    case '/events':
      return <EventsPage />
    case '/courses':
      return <CoursesPage />
    case '/ranking':
      return <RankingPage />
    case '/gallery':
      return <GalleryPage />
    case '/members':
      return <MembersPage />
    case '/dashboard':
      return <DashboardPage />
    case '/monetization':
      return <MonetizationPage />
    case '/campaigns':
      return <CampaignsPage />
    case '/workflows':
      return <WorkflowsPage />
    case '/logs':
      return <LogsPage />
    /* F-178 1b: Administração › Biblioteca de mídia. */
    case '/media-library':
      return <MediaLibraryPage />
    /* F-164 "Agendados": the posts waiting for their time (`?spaceId=` narrows to one space). */
    case '/scheduled':
      return <ScheduledPostsPage />
    case '/engagement':
      return <EngagementPage />
    case '/analytics':
      return <AnalyticsPage />
    case '/settings':
      return <SettingsPage />
    case '/account':
      return <AccountPage />
    case '/pricing':
      // The plan list compares plans side by side; a plan link (/pricing/{plan}/{option}) is the plan's checkout.
      return routePath === PRICING_PATH ? <PricingPlansPage /> : <CheckoutSubscriptionsPage />
    case '/products':
      return <CheckoutProductsPage />
    case ROUTES.adminFees:
      // SuperAdmin: installment interest table (F-43). The page checks the role; the API does too.
      return <AdminFeesPage />
    case ROUTES.adminPlatform:
      // SuperAdmin "Plataforma Memberfy" (F-34): the page checks the role; the API does too.
      return <AdminPlatformPage />
    case ROUTES.authSetPassword:
      // PROF-3: the invite link, public and without a session.
      return <SetPasswordPage />
    case ROUTES.supportEntry:
      // F-31 F2: the SuperAdmin's "Entrar" link, public; it opens the owner's session.
      return <SupportEntryPage />
    case '/checkout-success':
      return <CheckoutSuccessPage />
    default: {
      /* APP-55: `/{section}/{space}[/{item}]` that the menu does not list. The
         API hides a space this person cannot open (a `plan` space without a
         subscription), so a link from an e-mail lands here without any request
         to answer 403 — and drawing the home space instead said nothing. The
         API has no lookup by slug, so a mistyped link looks the same; the
         generic screen is worded for both. */
      const segments = routePath.split('/').filter(Boolean)
      /* F-161a: `/{section}`, the section page (a section padlocked as one line opens here). */
      const routeSection = segments.length === 1 ? sections.find((section) => section.slug === segments[0]) : undefined
      if (routeSection) return <SectionPage key={routeSection.id} section={routeSection} />
      const looksLikeSpace = (segments.length === 2 || segments.length === 3) && segments[0] !== '_'
      if (looksLikeSpace && !isLoadingMenu && sections.length > 0) {
        const [sectionSlug, spaceSlug] = segments
        const listed = sections.some(
          (section) => section.slug === sectionSlug && section.spaces?.some((space) => space.slug === spaceSlug)
        )
        /* Staff get every space in the menu (the API never hides one from them), so an unlisted
           link is a space that no longer exists; for anyone else it may also be a restricted one. */
        if (!listed) return seesWholeMenu
          ? <ItemUnavailable kind="missing" item="space" onBack={() => navigate('/')} className="mx-auto max-w-7xl" />
          : <SpaceAccessDenied denied={null} />
      }
      if (!defaultHomeSpace) {
        if (isLoadingMenu) {
          return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-600">{t('loading')}</p>
              </div>
            </div>
          )
        }
        return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
            <div className="max-w-sm text-center">
              <div className="w-14 h-14 mx-auto mb-5 bg-primary/10 rounded-2xl flex items-center justify-center">
                <Layers className="w-7 h-7 text-primary" />
              </div>
              {canEdit ? (
                <>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('emptyState.noSectionsTitle')}</h2>
                  <p className="text-sm text-gray-500 mb-6">{t('emptyState.noSectionsDescAdmin')}</p>
                  <Button
                    onClick={() => navigate('/settings')}
                    className="bg-primary text-white hover:bg-primary/90 text-sm"
                  >
                    {t('emptyState.goToSettings')}
                  </Button>
                </>
              ) : (
                <>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('emptyState.noSectionsTitle')}</h2>
                  <p className="text-sm text-gray-500">{t('emptyState.noSectionsDescMember')}</p>
                </>
              )}
            </div>
          </div>
        )
      }
      // Render appropriate page based on home space module
      return renderSpacePage(defaultHomeSpace.module, { spaceId: defaultHomeSpace.id })
    }
  }
}

const EventToastWrapper = () => {
  const { spaceData } = useRoute()
  // Only enable notifications for events module
  const isEventsModule = spaceData?.module === 'events'
  return <EventToastContainer spaceId={spaceData?.id} enabled={isEventsModule} />
}

const AppInitializer = () => {
  const { t } = useTranslation('common')
  const { setCommunity, isCommunityFound, setIsCommunityFound, community } = useCommunity()
  const { user, activeMember, isRoleResolved, setActiveCommunityId } = useAuth()
  const [isInitialized, setIsInitialized] = useState(false)
  /** Why the boot failed, when it did — the screen needs more than a boolean. */
  const [failure, setFailure] = useState<BootFailure | null>(null)

  useGoogleAnalytics(community?.googleAnalyticsId, community?.currency, community?.id)
  usePerformanceTracking(!!community?.googleAnalyticsId)

  useEffect(() => {
    const initializeCommunity = async () => {
      try {
        const domain = window.location.hostname
        const response = (await sdk.communities.getCommunityByDomain(domain)) as NonNullable<ReturnType<typeof useCommunity>['community']> | null

        if (response) {
          setCommunity(response)
          sdk.setCommunityId(response.id)
          /* The role comes from the session's member in this community only (APP-37). */
          setActiveCommunityId(response.id)
          setIsCommunityFound(true)

          setSentryCommunity({
            id: response.id,
            name: response.name,
            domain: response.domain,
          })
        } else {
          /* The server answered and said no: this one really is about the
             address the person typed. */
          console.error('Community not found for domain:', domain)
          setFailure({ kind: 'notFound' })
          setIsCommunityFound(false)
        }
      } catch (error) {
        console.error('Error initializing community:', error)
        setFailure(classifyBootFailure(error))
        setIsCommunityFound(false)
      } finally {
        setIsInitialized(true)
      }
    }

    initializeCommunity()
  }, [setCommunity, setIsCommunityFound, setActiveCommunityId])

  /* Mirrors the session's member, resolved by AuthContext from `/auth/me` and
     the community above, onto the community object and Sentry. */
  useEffect(() => {
    if (!user?.id) {
      clearSentryUser()
      return
    }
    if (!isRoleResolved) return

    setCommunity(previous => (previous ? { ...previous, userMember: activeMember } : previous))

    setSentryUser({
      id: user.id,
      email: user.email,
      username: (activeMember?.displayName || activeMember?.fullName) || user.email,
      role: activeMember?.role,
    })
  }, [user?.id, user?.email, isRoleResolved, activeMember, setCommunity])

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loadingCommunity')}</p>
        </div>
      </div>
    )
  }

  if (!isCommunityFound) {
    return (
      <BootErrorScreen
        failure={failure ?? { kind: 'notFound' }}
        domain={window.location.hostname}
      />
    )
  }

  return (
    <SubscriptionProvider>
      <MenuProvider>
        <RouteProvider>
          <SidebarProviderWithRoute>
            {/* F-110: one platform socket per tab; the chat, the bell and the live screens listen to it. */}
            <RealtimeProvider>
              {/* The menu follows other admins' changes without a reload. */}
              <MenuRealtimeSync />
              {/* F-108: the chat wraps the profile card, which offers "Enviar mensagem". */}
              <ChatProvider>
                <ProfileProvider>
                  {/* F-178: one podcast player per community session, above the module switch, so the sound
                      keeps going across pages and spaces; another community or account remounts it (stops). */}
                  <PlayerProvider key={`${community?.id ?? ''}:${user?.id ?? 'guest'}`} communityId={community?.id ?? ''} isAuthenticated={Boolean(user)}>
                    {/* F-178: the Vídeos element lives here too, so leaving the episode keeps it playing (floating, PiP). */}
                    <VideoDockProvider>
                      <AppLayout />
                    </VideoDockProvider>
                  </PlayerProvider>
                  <FlashToastHost />
                  {/* F-233: the code modal of the protected actions (403 STEP_UP_REQUIRED). */}
                  <StepUpHost />
                  <CookieNotice />
                </ProfileProvider>
              </ChatProvider>
            </RealtimeProvider>
          </SidebarProviderWithRoute>
        </RouteProvider>
      </MenuProvider>
    </SubscriptionProvider>
  )
}

const SidebarProviderWithRoute = ({ children }: { children: React.ReactNode }) => {
  const { currentPath } = useRoute()
  const { isAuthenticated } = useAuth()
  const { sections } = useMenu()
  const autoCollapsed = !isAuthenticated && sections.length === 0
  return <SidebarProvider currentPath={currentPath} autoCollapsed={autoCollapsed}>{children}</SidebarProvider>
}

const AppLayout = () => {
  const { currentPath } = useRoute()
  /* F-103 §7a: the public link is full screen, in the survey community's colors, outside the shell. */
  if (isPublicSurveyPath(currentPath)) {
    return <><Suspense fallback={<PageFallback />}><PublicSurveyPage /></Suspense><MiniPlayer /></>
  }
  /* F-109: a form's public link, the same way. */
  if (isPublicFormPath(currentPath)) {
    return <><Suspense fallback={<PageFallback />}><PublicFormPage /></Suspense><MiniPlayer /></>
  }
  return <ShellLayout />
}

const ShellLayout = () => {
  const { isCollapsed, isTopNav } = useSidebar()
  /* F-108: the chat docked at full height on the right; the page makes room for it. */
  const chatSideOpen = useChatSideOpen()
  const { mustChangePassword, clearMustChangePassword, user } = useAuth()
  const { t } = useTranslation('auth')
  const [showSuccess, setShowSuccess] = React.useState(false)

  const handlePasswordChangeSuccess = () => {
    setShowSuccess(true)
    clearMustChangePassword()
    setTimeout(() => {
      setShowSuccess(false)
    }, 3000)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <Sidebar />
      {/* F-191 phase 2: the compact menu, from 1024 px, when the member or the community picked it. */}
      <SidebarRail />
      {/* F-191 phase 5: with the bar on top, the phones' section strip under the header. */}
      {isTopNav && <TopNavStrip />}
      <main className={cn(
        "dock-clear pt-[--app-top] transition-all duration-200",
        !isCollapsed && "lg:ml-[--sidebar-width]",
        chatSideOpen && CHAT_SIDE_WIDTH_CLASS
      )}>
        {/* F-86: the leadership's alarm while the SuperAdmin keeps the community suspended. */}
        <SuspensionBanner />
        {/* F-143: Campaigns and Automações suspended (or close to it) by unsubscribes or complaints; team only. */}
        <SendingReputationBanner />
        {/* F-199: the account's erasure is scheduled and paid access keeps the session until the date. */}
        <ErasureBanner />
        {/* F-233: the account must turn two-step verification on (team: the deadline, then blocked; members: asked). */}
        <TwoFactorBanner />
        {/* F-186: install the app and turn notifications on (modal after login, then a thin bar). */}
        <PushPrompts />
        {/* Invites a person without a photo here to complete their profile; quiet while the banner above shows. */}
        <ProfilePhotoBar />
        {/* Area presence: this tab's place in the screen's room, and the faces in its header. */}
        <ScreenPresenceProvider>
          <Suspense fallback={<PageFallback />}>
            {/* APP-162: the page remounts when the session changes, so the Feed and the lists
                load again with the new token (canEdit, canDelete come per item from the API). */}
            <AppContent key={user?.id ?? 'guest'} />
          </Suspense>
        </ScreenPresenceProvider>
        {isTopNav && <TopNavFooter />}
      </main>
      {/* F-108 Mensagens: the desktop panel (no menu entry) and the mobile full-screen sheet. */}
      {/* F-178: the podcast keeps playing here while the person navigates. */}
      <MiniPlayer />
      <ChatDock />
      <ChatMobileSheet />
      <EventToastWrapper />
      {/* F-104: what a publication did with its e-mail notice. */}
      <EmailNoticeToaster />
      {/* F-164: "Agendado para …" after a post is scheduled. */}
      <ScheduledToaster />
      {/* A save another person made stale (409): reload or keep mine. */}
      <StaleSaveDialogHost />
      <PlanLimitToaster />
      {/* F-61: a newer app or API contract than this tab is running. */}
      <UpdateNotice />
      {/* Bulk gallery upload keeps going while its dialog is closed. */}
      <GalleryUploadPill />
      <DebugPanel />
      <ChangePasswordDialog open={mustChangePassword} onSuccess={handlePasswordChangeSuccess} />
      {/* F-155: the community's welcome on the first access, before everything else ("Complete seu cadastro" waits). */}
      <WelcomeGate />
      {/* F-85: required custom fields the member has not filled in yet. */}
      <CompleteProfileModal />
      {/* F-103 1b: the open surveys the member has not answered, in one notice. */}
      <PendingSurveysNotice />
      {showSuccess && (
        <div className="fixed top-20 right-4 z-50 backdrop-blur-sm border rounded-xl p-4 bg-green-50/90 border-green-100/50 shadow-lg">
          <p className="text-base font-medium text-green-600">{t('passwordChanged')}</p>
        </div>
      )}
    </div>
  )
}

function AppRoot() {
  const [sdkStatus, setSdkStatus] = useState<'loading' | 'error' | 'ready'>('loading')
  const [sdkError, setSdkError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    let cancelled = false
    setSdkStatus('loading')
    setSdkError(null)

    sdk.initialize()
      .then(() => {
        if (!cancelled) setSdkStatus('ready')
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setSdkError((error as Error | null)?.message || null)
          setSdkStatus('error')
        }
      })

    return () => {
      cancelled = true
    }
  }, [retryCount])

  return (
    <SdkGate status={sdkStatus} error={sdkError} onRetry={() => setRetryCount(c => c + 1)}>
      <ErrorBoundary>
        <LocaleProvider>
          <Suspense fallback={
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          }>
            <ThemeProvider>
              <CommunityProvider>
                <AuthProvider>
                  <AppInitializer />
                </AuthProvider>
              </CommunityProvider>
            </ThemeProvider>
          </Suspense>
        </LocaleProvider>
      </ErrorBoundary>
    </SdkGate>
  )
}

/**
 * F-78 1B: the visitor kiosk is a public full-screen route, outside the shell, the community boot and any
 * session. It decides before `AppRoot` mounts its providers, so nothing of the member's session is touched.
 */
function App() {
  /* F-178 phase 2: the episode and show embed, public and outside the shell, decided on the same terms. */
  if (isMediaEmbedPath(window.location.pathname)) {
    return <Suspense fallback={null}><MediaEmbedPage /></Suspense>
  }
  /* F-79 1C: the room's door panel is public and full screen too, decided on the same terms. */
  if (isRoomPanelPath(window.location.pathname)) {
    return <Suspense fallback={<PageFallback />}><RoomPanelPage /></Suspense>
  }
  /* F-115: an event's door kiosk, public and full screen on the same terms. */
  if (isEventKioskPath(window.location.pathname)) {
    return <Suspense fallback={<PageFallback />}><EventKioskPage /></Suspense>
  }
  return isKioskPath(window.location.pathname)
    ? <Suspense fallback={<PageFallback />}><KioskPage /></Suspense>
    : <AppRoot />
}

export default App